export type ThemeColors = {
  primary: string;
  secondary: string;
  tertiary: string;
  background: string;
};

/**
 * Default ("current") theme. Used as a fallback when the mobile-app colors
 * endpoint fails, and as the values the CSS custom properties fall back to
 * before any colors are applied. Mirrors the defaults in index.css.
 */
export const DEFAULT_THEME_COLORS: ThemeColors = {
  primary: "#ffffff",
  secondary: "#000000",
  tertiary: "#ff6500",
  background: "#000000",
};

/**
 * Toggles the global grayscale filter (see #root.theme-loading in App.css).
 * The app renders desaturated until the real (or fallback) colors are applied.
 */
export function setThemeLoading(loading: boolean) {
  const root = document.getElementById("root");
  if (!root) return;
  root.classList.toggle("theme-loading", loading);
}

/**
 * Parses a color string to [r, g, b]. Accepts the formats the backend may
 * send: `rgb(r, g, b)` / `rgba(r, g, b, a)` and hex (`#rgb`, `#rrggbb`,
 * `#rrggbbaa`).
 */
function parseColor(color: string): [number, number, number] {
  const c = color.trim();

  const rgbMatch = c.match(/rgba?\(([^)]+)\)/i);
  if (rgbMatch) {
    const [r, g, b] = rgbMatch[1].split(",").map((p) => parseFloat(p.trim()));
    return [r || 0, g || 0, b || 0];
  }

  const h = c.replace("#", "");
  const full =
    h.length === 3
      ? h
          .split("")
          .map((ch) => ch + ch)
          .join("")
      : h;
  return [
    parseInt(full.substring(0, 2), 16) || 0,
    parseInt(full.substring(2, 4), 16) || 0,
    parseInt(full.substring(4, 6), 16) || 0,
  ];
}

/**
 * Blends `color` towards `target` at the given ratio.
 * ratio = 1 → fully `color`;  ratio = 0 → fully `target`.
 * result_channel = color * ratio + target * (1 - ratio)
 */
function blendTowards(color: string, target: string, ratio: number): string {
  const [r, g, b] = parseColor(color);
  const [tr, tg, tb] = parseColor(target);
  const toHex = (v: number) => Math.round(v).toString(16).padStart(2, "0");
  const mix = (c: number, t: number) => c * ratio + t * (1 - ratio);
  return `#${toHex(mix(r, tr))}${toHex(mix(g, tg))}${toHex(mix(b, tb))}`;
}

/**
 * Returns an rgba() string for the given color and opacity.
 */
function rgba(color: string, opacity: number): string {
  const [r, g, b] = parseColor(color);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

/**
 * Takes the 4 theme colors from the backend and sets all derived
 * CSS custom properties (shades, transparent variants) on :root.
 */
export function applyThemeColors(colors: {
  primary: string;
  secondary: string;
  tertiary: string;
  background: string;
}) {
  const root = document.documentElement;
  const { primary, secondary, tertiary, background } = colors;

  // ── Base colors ──
  root.style.setProperty("--color-primary", primary);
  root.style.setProperty("--color-secondary", secondary);
  root.style.setProperty("--color-tertiary", tertiary);
  root.style.setProperty("--color-background", background);

  // ── Primary (white) shades — blend towards background ──
  root.style.setProperty(
    "--white-shade-1",
    blendTowards(primary, background, 0.7),
  );
  root.style.setProperty(
    "--white-shade-2",
    blendTowards(primary, background, 0.5),
  );
  root.style.setProperty(
    "--white-shade-3",
    blendTowards(primary, background, 0.4),
  );
  root.style.setProperty(
    "--white-shade-4",
    blendTowards(primary, background, 0.3),
  );

  // ── Tertiary (orange) shades — blend towards background ──
  root.style.setProperty(
    "--orange-shade-1",
    blendTowards(tertiary, background, 0.7),
  );
  root.style.setProperty(
    "--orange-shade-2",
    blendTowards(tertiary, background, 0.5),
  );
  root.style.setProperty(
    "--orange-shade-3",
    blendTowards(tertiary, background, 0.4),
  );
  root.style.setProperty(
    "--orange-shade-4",
    blendTowards(tertiary, background, 0.3),
  );

  // ── Transparent primary (white) ──
  for (let i = 1; i <= 6; i++) {
    root.style.setProperty(`--transparent-white-${i}`, rgba(primary, i * 0.1));
  }

  // ── Transparent secondary (black) ──
  for (let i = 1; i <= 6; i++) {
    root.style.setProperty(
      `--transparent-black-${i}`,
      rgba(secondary, i * 0.1),
    );
  }

  // ── Transparent tertiary (orange) ──
  for (let i = 1; i <= 6; i++) {
    root.style.setProperty(
      `--transparent-orange-${i}`,
      rgba(tertiary, i * 0.1),
    );
  }
}
